import * as React from 'react';
import BPromise from 'bluebird';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { parse } from 'query-string';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from 'Src/helpers/table/table';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { History, Location } from 'history';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { Loader } from 'Src/components/Loader/Loader';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { getTournament } from 'Src/helpers/service/adminSU/tournamentsTable';
import { Tournament } from 'Src/models/tournament';
import {
  addSchoolTournamentTeam,
  editSchoolTournamentTeam,
  getAllSchoolTournamentTeams,
  getSchoolTournamentTeams,
  getSchoolTournamentTeamsCount,
  removeSchoolTournamentTeam
} from 'Src/helpers/service/admin/tournamentTeams';
import { getTeamAges, getTeamGender } from 'Src/helpers/accessor/accessor';
import { getSelectOptionForTournamentTeamAges, getSelectOptionForTournamentTeamGender } from 'Src/helpers/table/select';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { TEAM_TYPE } from 'Src/consts/team';
import { isIndividualTournament } from 'Src/helpers/tournament/tournament';
import { TournamentTeamForm } from './TournamentTeamForm/TournamentTeamForm';
import { TournamentTeam } from 'Src/models/tournamentTeam';
import { formatAgesCompact, resolveAgeNamingFromContext } from 'Src/helpers/ageGroups';
import '../Invites/SchoolInvitesHub.scss';
import './TournamentEvents.css';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name',
    field: 'name',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['name']
  },
  {
    text: 'Gender',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getTeamGender
  },
  {
    text: 'Age',
    field: 'teamAges',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getTeamAges
  }
];

interface State {
  items: TournamentTeam[];
  currentPage: number;
  selectedItems: TournamentTeam[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  filters: any;
  isDataFiltered: boolean;
  isLoading: boolean;
  total: number;
  basePath: string;
  isSelectAllChecked: boolean;
  tournament: Tournament;

  isAddTeamModalOpen: boolean;
  isEditTeamModalOpen: boolean;
  isDeleteTeamErrorModalOpen: boolean;
  isDeleteTeamConfirmationModalOpen: boolean;
  isTeamSubmitting: boolean;
  teamMutationError: string;

  allItems: TournamentTeam[];
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class TournamentTeams extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      filters: {},
      isDataFiltered: false,
      isLoading: true,
      total: 0,
      basePath: '',
      isSelectAllChecked: false,
      tournament: undefined,

      isAddTeamModalOpen: false,
      isEditTeamModalOpen: false,
      isDeleteTeamErrorModalOpen: false,
      isDeleteTeamConfirmationModalOpen: false,
      isTeamSubmitting: false,
      teamMutationError: '',
      allItems: []
    };
  }

  componentDidMount() {
    if (!this.getTournamentId()) {
      const prevRoutePath = propz.get(this.props.location, ['state', 'prevRoutePath'], '/tournaments/upcoming');
      const search = propz.get(this.props.location, ['state', 'search'], '');
      this.props.history.replace({ pathname: prevRoutePath || '/tournaments/upcoming', search });
      return;
    }

    this.setState({
      isLoading: true
    });

    this.setAdditionalItems().then(res => {
      this.setItems();
    });
  }

  componentWillUnmount() {
    window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb-clear'));
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const isSameRoute = nextProps.location.pathname === this.props.location.pathname;
    const nextTournamentId = this.getTournamentId(nextProps);
    if (isSameRoute && nextTournamentId && nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true
      });
      this.setItems(nextProps);
    }
  }

  getTournamentId(props = this.props): string {
    const search = parse(props.location.search);
    const tournamentIdFromSearch = search.tournamentId;
    const tournamentIdFromState = propz.get(props.location, ['state', 'tournamentId'], '');
    const tournamentId = String(tournamentIdFromSearch || tournamentIdFromState || '');
    return tournamentId === 'undefined' || tournamentId === 'null' ? '' : tournamentId;
  }

  setAdditionalItems() {
    const { user } = this.props;

    const tournamentId = this.getTournamentId();

    return BPromise.all([getTournament(user, tournamentId), getAllSchoolTournamentTeams(user, tournamentId)]).then(
      ([tournament, allItems]) => {
        this.setState({
          tournament: tournament,
          allItems: allItems
        });
        window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb', { detail: `${tournament.name} / Manage teams` }));

        return true;
      }
    );
  }

  setItems(props = this.props) {
    const { user, location } = props;

    const search = parse(location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(COLUMNS, search);

    const isDataFiltered = isFilterExist2(filters);

    const where = getServerFieldSectionWhere(filters);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const tournamentId = this.getTournamentId(props);

    if (!tournamentId) {
      return BPromise.resolve(false);
    }

    const getItemsPromise = getSchoolTournamentTeams(user, tournamentId, serverQueryFilter);
    const getItemsCountPromise = getSchoolTournamentTeamsCount(user, tournamentId, where);

    return BPromise.all([getItemsCountPromise, getItemsPromise]).then(([countObj, items]) => {
      this.setState({
        currentPage: page,
        sortDirection: sortDirection,
        sortColumnsName: sortColumnsName,
        isShowFilter: false,
        isDataFiltered: isDataFiltered,
        filters: filters,
        items: items,
        total: countObj.count,
        basePath: location.pathname,
        isLoading: false
      });

      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    const tournamentId = this.getTournamentId();

    this.props.history.push({
      pathname: this.state.basePath,
      search: `tournamentId=${tournamentId}&${search.join('&')}`
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: nextSelectedItems,
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true
    });

    const user = this.props.user;

    const where = getServerFieldSectionWhere(filters);

    const tournamentId = this.getTournamentId();

    getAllSchoolTournamentTeams(user, tournamentId, where).then(items => {
      const itemsFiltered = items.filter(item => {
        return selectedItems.every(selectedItem => selectedItem.id !== item.id);
      });

      const nextSelectedItems = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: nextSelectedItems,
        isSelectAllChecked: true,
        isLoading: false
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    const tournamentId = this.getTournamentId();

    this.props.history.push({
      pathname: this.state.basePath,
      search: `tournamentId=${tournamentId}&${search.join('&')}`
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (options && options.modernMultiValue) {
      nextFilters = { ...nextFilters, [filterField]: filterValue };
    } else if (options && options.modernRange) {
      nextFilters = { ...nextFilters, [filterField]: options.modernRange };
    } else if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      filters: nextFilters
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false,
      filters: getFilters2(COLUMNS, {})
    });

    const tournamentId = this.getTournamentId();

    this.props.history.push({
      pathname: this.state.basePath,
      search: `tournamentId=${tournamentId}&${search.join('&')}`
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    const tournamentId = this.getTournamentId();

    this.props.history.push({
      pathname: this.state.basePath,
      search: `tournamentId=${tournamentId}`
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter
    });
  };

  onCancelFilterClick = (): void => {
    this.setState({ isShowFilter: false, filters: getFilters2(COLUMNS, parse(this.props.history.location.search)) });
  };

  isOneItemSelected(): boolean {
    const selectedItems = this.state.selectedItems;
    return selectedItems.length === 1;
  }

  goBack = () => {
    const { history, location } = this.props;
    const state = (location.state || {}) as any;
    const { search = '', isGoBackWithoutSave, prevRoutePath = '/tournaments/upcoming' } = state;
    const tournamentId = isGoBackWithoutSave ? undefined : this.getTournamentId();

    const tournamentManagementPaths = [
      '/tournaments/teams',
      '/tournaments/teamPlayers',
      '/tournaments/teamPlayers/manage'
    ];
    const isValidListRoute =
      typeof prevRoutePath === 'string' &&
      prevRoutePath.length > 0 &&
      !tournamentManagementPaths.includes(prevRoutePath);
    const tournamentEndTime = this.state.tournament && this.state.tournament.endTime;
    const fallbackPath =
      tournamentEndTime && new Date(tournamentEndTime).getTime() < Date.now()
        ? '/tournaments/archive'
        : '/tournaments/upcoming';

    history.push({
      pathname: isValidListRoute ? prevRoutePath : fallbackPath,
      search,
      state: { ...state, tournamentId }
    });
  };

  isAddTeamActive(): boolean {
    const { tournament, allItems, selectedItems } = this.state;

    const isSelectedItemsEmpty = selectedItems.length === 0;
    const totalItemsCount = allItems.length;
    if (!isSelectedItemsEmpty || !tournament) return false;

    if (isIndividualTournament(tournament)) {
      return totalItemsCount === 0;
    }

    const maximumNumberTeamsOfSchool = Number(tournament.maximumNumberTeamsOfSchool || 0);
    const isSchoolTeamLimitDisabled = maximumNumberTeamsOfSchool === 0;

    return totalItemsCount === 0 || isSchoolTeamLimitDisabled || totalItemsCount < maximumNumberTeamsOfSchool;
  }

  onDeleteTeamClick = (): void => {
    const { selectedItems: teams, allItems } = this.state;
    const teamsCount = teams.length;
    const diff = allItems.length - teamsCount;

    if (diff > 0) {
      this.setState({
        isDeleteTeamConfirmationModalOpen: true,
        teamMutationError: ''
      });
    } else {
      this.setState({
        isDeleteTeamErrorModalOpen: true
      });
    }
  };

  onEditTeamPlayersClick = () => {
    const { history, location } = this.props;
    const { selectedItems } = this.state;
    const { state } = location;
    const { prevRoutePath } = state as any;
    const team = selectedItems[0];
    const { id } = team;
    const tournamentId = this.getTournamentId();

    history.push({
      pathname: `/tournaments/teamPlayers/manage`,
      search: `teamId=${id}&tournamentId=${tournamentId}`,
      state: { ...(state as any), prevRoutePath, teamsSearch: location.search }
    });
  };

  renderGrid() {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      isDataFiltered,
      selectedItems,
      filters,
      isSelectAllChecked,
      currentPage,
      total,
      tournament
    } = this.state;

    const ageNaming = resolveAgeNamingFromContext(this.props.user, tournament);
    const columns = COLUMNS.map(column =>
      column.field === 'teamAges'
        ? {
            ...column,
            accessor: (team: TournamentTeam) => formatAgesCompact(team.ages, ageNaming) || '-'
          }
        : column
    );

    if (tournament && tournament.maxPlayersPerTeam && tournament.maxPlayersPerTeam !== 0) {
      columns.push({
        text: 'Students',
        field: 'numberOfStudents',
        isSort: false,
        type: FILTER_TYPE.TEXT,
        accessor: team => `${team.numberOfStudents || 0} / ${tournament.maxPlayersPerTeam}`
      });
    }

    const { isReadOnly } = tournament;

    const isRosterReadOnly =
      isReadOnly && String(tournament.schoolId || '') !== String(this.props.user.activeSchoolId || '');

    const actionItems = [
      {
        itemText: 'Add team',
        onItemClick: this.onAddTeamClick,
        isActive: this.isAddTeamActive() && !isReadOnly
      },
      {
        itemText: 'Edit team',
        onItemClick: this.onEditTeamClick,
        isActive: selectedItems.length === 1 && !isReadOnly
      },
      {
        itemText: isRosterReadOnly ? 'View participants & allocations' : 'Edit participants',
        onItemClick: isRosterReadOnly ? this.onAllocateToEventsClick : this.onEditTeamPlayersClick,
        isActive: selectedItems.length === 1 && (!isReadOnly || isRosterReadOnly)
      },
      ...(!isRosterReadOnly ? [{
        itemText: 'Allocate to events',
        onItemClick: this.onAllocateToEventsClick,
        isActive: selectedItems.length === 1
      }] : []),
      {
        itemText: selectedItems.length > 1 ? 'Delete teams' : 'Delete team',
        onItemClick: this.onDeleteTeamClick,
        isActive: selectedItems.length > 0 && !isReadOnly,
        isDanger: true
      }
    ];

    const filterOptions = {
      gender: getSelectOptionForTournamentTeamGender(),
      teamAges: getSelectOptionForTournamentTeamAges(tournament, this.props.user)
    };
    const gridTitle = typeof tournament !== 'undefined' ? 'School teams' : '';

    const { user } = this.props;

    return (
      <Grid2
        isActionsInline={true}
        selectionActionsBelow={true}
        gridTitleCount={total}
        toolbarSearchField="name"
        toolbarSearchPlaceholder="Search teams"
        selectedItemsCount={selectedItems.length}
        isCSV={false}
        dataItems={items}
        filters={filters}
        currentPage={currentPage}
        total={total}
        isSelectAllChecked={isSelectAllChecked}
        isDataFiltered={isDataFiltered}
        sortDirection={sortDirection}
        sortColumnsName={sortColumnsName}
        isShowFilter={isShowFilter}
        dataItemsSelected={selectedItems}
        columns={columns}
        actionItems={actionItems}
        options={filterOptions}
        onItemClick={this.onItemClick}
        onSortClick={this.onTableSortClick}
        onApplyFilterClick={this.onApplyFilterClick}
        onClearFilterClick={this.onClearFilterClick}
        onCancelFilterClick={this.onCancelFilterClick}
        onTableFilterChange={this.onTableFilterChange}
        onTableFilterClick={this.onTableFilterClick}
        setCurrentPageParams={this.setCurrentPageParams}
        onSelectAllClick={this.onSelectAllClick}
        onSelectAllOnPageClick={this.onSelectAllOnPageClick}
        onUnselectAllClick={this.onUnselectAllClick}
        gridTitle={gridTitle}
        user={user}
        tournament={tournament}
        columnVisibilityKey="tournaments.school.teams"
        fixedColumnFields={['name']}
        emptyText={isDataFiltered ? 'No teams match your filters' : 'No teams added yet'}
        emptyHint={
          isDataFiltered ? 'Try changing your search or filters.' : 'Use Add team to create the first tournament team.'
        }
        stylizeCell={({ column }) => `tournamentTeamsModern__cell tournamentTeamsModern__cell--${column.field}`}
      />
    );
  }

  onAddTeamFormCancel = (): void => {
    if (this.state.isTeamSubmitting) return;
    this.setState({
      isAddTeamModalOpen: false,
      teamMutationError: ''
    });
  };

  onAddTeamClick = (): void => {
    this.setState({
      isAddTeamModalOpen: true,
      teamMutationError: ''
    });
  };

  onEditTeamFormCancel = (): void => {
    if (this.state.isTeamSubmitting) return;
    this.setState({
      isEditTeamModalOpen: false,
      teamMutationError: ''
    });
  };

  onEditTeamClick = (): void => {
    this.setState({
      isEditTeamModalOpen: true,
      teamMutationError: ''
    });
  };

  onAddTeamFormSubmit = (data: any) => {
    const { name, numberOfStudents } = data;
    const { tournament } = this.state;
    const { user } = this.props;
    const { activeSchoolId: schoolId } = user;

    const { sportId } = tournament;
    const tournamentId = this.getTournamentId();

    const parsedNumberOfStudents = Number(numberOfStudents);
    const normalizedNumberOfStudents = Number.isFinite(parsedNumberOfStudents) ? parsedNumberOfStudents : 0;

    if (
      Number(tournament.maxPlayersPerTeam) > 0 &&
      (!Number.isInteger(parsedNumberOfStudents) ||
        parsedNumberOfStudents < 1 ||
        parsedNumberOfStudents > Number(tournament.maxPlayersPerTeam))
    ) {
      this.setState({
        teamMutationError: `Enter a whole number between 1 and ${tournament.maxPlayersPerTeam}.`
      });
      return;
    }

    this.setState({ isTeamSubmitting: true, teamMutationError: '' });

    let teamData;

    switch (true) {
      case isIndividualTournament(tournament): {
        const { gender, ages } = tournament;
        teamData = {
          name,
          ages,
          gender,
          sportId,
          schoolId,
          teamType: TEAM_TYPE.ADHOC,
          players: [],
          numberOfStudents: normalizedNumberOfStudents
        };
        break;
      }

      default: {
        const { age, gender } = data;
        teamData = {
          name,
          gender,
          sportId,
          schoolId,
          teamType: TEAM_TYPE.ADHOC,
          players: [],
          ages: [Number(age)],
          numberOfStudents: normalizedNumberOfStudents
        };
      }
    }

    addSchoolTournamentTeam(user, tournamentId, teamData)
      .then(() => {
        this.setState({ isTeamSubmitting: false, isAddTeamModalOpen: false, teamMutationError: '' });
        return this.setAdditionalItems();
      })
      .then(() => this.setItems())
      .catch(error => {
        const message = propz.get(
          error,
          ['response', 'data', 'message'],
          'The team could not be added. Please try again.'
        );
        this.setState({ isTeamSubmitting: false, teamMutationError: String(message) });
      });
  };

  onEditTeamFormSubmit = (data: any) => {
    const { name, numberOfStudents } = data;
    const { user } = this.props;
    const tournamentId = this.getTournamentId();

    const { selectedItems, tournament } = this.state;
    const team = selectedItems[0];
    const { id: teamId } = team;

    const parsedNumberOfStudents = Number(numberOfStudents);
    const normalizedNumberOfStudents = Number.isFinite(parsedNumberOfStudents) ? parsedNumberOfStudents : 0;

    if (
      Number(tournament.maxPlayersPerTeam) > 0 &&
      (!Number.isInteger(parsedNumberOfStudents) ||
        parsedNumberOfStudents < 1 ||
        parsedNumberOfStudents > Number(tournament.maxPlayersPerTeam))
    ) {
      this.setState({
        teamMutationError: `Enter a whole number between 1 and ${tournament.maxPlayersPerTeam}.`
      });
      return;
    }

    this.setState({ isTeamSubmitting: true, teamMutationError: '' });

    let teamData;

    switch (true) {
      case isIndividualTournament(tournament): {
        teamData = {
          name,
          numberOfStudents: normalizedNumberOfStudents
        };
        break;
      }

      default: {
        const { age, gender } = data;
        teamData = {
          name,
          gender,
          ages: [Number(age)],
          numberOfStudents: normalizedNumberOfStudents
        };
      }
    }

    editSchoolTournamentTeam(user, tournamentId, teamId, teamData)
      .then(team => {
        this.setState({
          isTeamSubmitting: false,
          isEditTeamModalOpen: false,
          teamMutationError: '',
          selectedItems: [team]
        });
        return this.setAdditionalItems();
      })
      .then(() => this.setItems())
      .catch(error => {
        const message = propz.get(
          error,
          ['response', 'data', 'message'],
          'The team could not be updated. Please try again.'
        );
        this.setState({ isTeamSubmitting: false, teamMutationError: String(message) });
      });
  };

  onAllocateToEventsClick = () => {
    const { location } = this.props;
    const { state } = location;
    const search = propz.get(state, ['search'], '');
    const prevRoutePath = propz.get(state, ['prevRoutePath'], '');
    const { selectedItems } = this.state;
    const team = selectedItems[0];
    const { id, name } = team;
    const tournamentId = this.getTournamentId();

    this.props.history.push({
      pathname: `/tournaments/teamPlayers`,
      search: `teamId=${id}&tournamentId=${tournamentId}`,
      state: {
        ...(state as any),
        search,
        teamName: name,
        prevRoutePath,
        tournamentId,
        isRosterReadOnly: Boolean(
          this.state.tournament &&
            this.state.tournament.isReadOnly &&
            String(this.state.tournament.schoolId || '') !== String(this.props.user.activeSchoolId || '')
        )
      }
    });
  };

  renderAddTeamModal(): React.ReactNode {
    const { isAddTeamModalOpen, tournament, allItems, isTeamSubmitting, teamMutationError } = this.state;
    const { user } = this.props;

    return (
      <SimpleModal
        isOpen={isAddTeamModalOpen}
        showHeader={false}
        showFooter={false}
        bodyClassName="tournamentTeamModalHost"
        dialogStyle={{ maxWidth: 620 }}
        onBackdropClick={this.onAddTeamFormCancel}
      >
        <TournamentTeamForm
          tournament={tournament}
          onSubmit={this.onAddTeamFormSubmit}
          onCancel={this.onAddTeamFormCancel}
          user={user}
          allSchoolTeams={allItems}
          isSubmitting={isTeamSubmitting}
          submissionError={teamMutationError}
        />
      </SimpleModal>
    );
  }

  renderEditTeamModal(): React.ReactNode {
    const {
      isEditTeamModalOpen,
      tournament,
      allItems,
      selectedItems,
      isTeamSubmitting,
      teamMutationError
    } = this.state;
    const { user } = this.props;
    const team = selectedItems[0];

    return (
      <SimpleModal
        isOpen={isEditTeamModalOpen}
        showHeader={false}
        showFooter={false}
        bodyClassName="tournamentTeamModalHost"
        dialogStyle={{ maxWidth: 620 }}
        onBackdropClick={this.onEditTeamFormCancel}
      >
        <TournamentTeamForm
          tournament={tournament}
          onSubmit={this.onEditTeamFormSubmit}
          onCancel={this.onEditTeamFormCancel}
          user={user}
          team={team}
          allSchoolTeams={allItems}
          isSubmitting={isTeamSubmitting}
          submissionError={teamMutationError}
        />
      </SimpleModal>
    );
  }

  onCloseDeleteTeamErrorModalClick = (): void => {
    this.setState({
      isDeleteTeamErrorModalOpen: false
    });
  };

  renderDeleteTeamErrorModal(): React.ReactNode {
    const { isDeleteTeamErrorModalOpen, tournament } = this.state;

    return (
      <SimpleModal
        isOpen={isDeleteTeamErrorModalOpen}
        showHeader={false}
        showFooter={false}
        bodyClassName="tournamentTeamActionModalHost"
        dialogStyle={{ maxWidth: 520 }}
        onBackdropClick={this.onCloseDeleteTeamErrorModalClick}
      >
        <section className="tournamentTeamActionModal isWarning" role="dialog" aria-label="Team cannot be removed">
          <header className="tournamentTeamActionModal__head">
            <span className="tournamentTeamActionModal__icon" aria-hidden="true">
              !
            </span>
            <div>
              <h2>Team cannot be removed</h2>
              <p>Your school must keep a team while it remains entered.</p>
            </div>
            <button type="button" onClick={this.onCloseDeleteTeamErrorModalClick} aria-label="Close">
              ×
            </button>
          </header>
          <div className="tournamentTeamActionModal__body">
            <div className="tournamentTeamActionModal__message">
              This is your school's only team in {tournament ? tournament.name : 'this tournament'}. To remove it,
              withdraw your school from the tournament first.
            </div>
          </div>
          <footer className="tournamentTeamActionModal__foot">
            <button type="button" className="isPrimary" onClick={this.onCloseDeleteTeamErrorModalClick}>
              Close
            </button>
          </footer>
        </section>
      </SimpleModal>
    );
  }

  onCloseDeleteTeamConfirmationModalClick = (): void => {
    this.setState({
      isDeleteTeamConfirmationModalOpen: false,
      teamMutationError: ''
    });
  };

  onDeleteTeamModalClick = (): void => {
    const { selectedItems: teams } = this.state;
    const { user } = this.props;
    const tournamentId = this.getTournamentId();

    BPromise.all(teams.map(team => removeSchoolTournamentTeam(user, tournamentId, team.id)))
      .then(res => {
        this.setState({
          isDeleteTeamConfirmationModalOpen: false,
          selectedItems: [],
          isSelectAllChecked: false,
          teamMutationError: ''
        });
        this.setAdditionalItems().then(res => {
          this.setItems();
        });
      })
      .catch(error => {
        const message = propz.get(
          error,
          ['response', 'data', 'message'],
          'The team could not be removed. Please try again.'
        );
        this.setState({ teamMutationError: String(message) });
      });
  };

  renderDeleteTeamConfirmationModal(): React.ReactNode {
    const { isDeleteTeamConfirmationModalOpen, selectedItems: teams, tournament, teamMutationError } = this.state;
    const isMultiple = teams.length > 1;
    const selectedTeamName = propz.get(teams, ['0', 'name'], 'this team');

    return (
      <SimpleModal
        isOpen={isDeleteTeamConfirmationModalOpen}
        showHeader={false}
        showFooter={false}
        bodyClassName="tournamentTeamActionModalHost"
        dialogStyle={{ maxWidth: 520 }}
        onBackdropClick={this.onCloseDeleteTeamConfirmationModalClick}
      >
        <section className="tournamentTeamActionModal isDanger" role="dialog" aria-label="Remove tournament team">
          <header className="tournamentTeamActionModal__head">
            <span className="tournamentTeamActionModal__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M4 7h16M9 7V4h6v3M7 7l1 13h8l1-13M10 11v5M14 11v5" />
              </svg>
            </span>
            <div>
              <h2>{isMultiple ? 'Remove teams?' : 'Remove team?'}</h2>
              <p>Review this action before continuing.</p>
            </div>
            <button type="button" onClick={this.onCloseDeleteTeamConfirmationModalClick} aria-label="Close">
              ×
            </button>
          </header>
          <div className="tournamentTeamActionModal__body">
            <div className="tournamentTeamActionModal__message">
              {isMultiple
                ? `Remove ${teams.length} selected teams from ${tournament ? tournament.name : 'this tournament'}?`
                : `Remove ${selectedTeamName} from ${tournament ? tournament.name : 'this tournament'}?`}
            </div>
            <p>This removes the team from the tournament and cannot be undone.</p>
            {teamMutationError ? (
              <div className="tournamentTeamForm__error" role="alert">
                {teamMutationError}
              </div>
            ) : null}
          </div>
          <footer className="tournamentTeamActionModal__foot">
            <button type="button" onClick={this.onCloseDeleteTeamConfirmationModalClick}>
              Cancel
            </button>
            <button type="button" className="isDanger" onClick={this.onDeleteTeamModalClick}>
              {isMultiple ? 'Remove teams' : 'Remove team'}
            </button>
          </footer>
        </section>
      </SimpleModal>
    );
  }

  render() {
    const {
      isLoading,
      isAddTeamModalOpen,
      isDeleteTeamErrorModalOpen,
      isDeleteTeamConfirmationModalOpen,
      isEditTeamModalOpen,
      tournament,
      total
    } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const classes =
      isAddTeamModalOpen || isDeleteTeamErrorModalOpen || isDeleteTeamConfirmationModalOpen || isEditTeamModalOpen
        ? 'modal-open'
        : '';

    return (
      <div className={`${classes} schoolInvitesList tournamentTeamsModern`}>
        <header className="tournamentListPageHeader">
          <button type="button" onClick={this.goBack}>
            <span aria-hidden="true">←</span> Tournaments
          </button>
          <i aria-hidden="true" />
          <div>
            <small>{tournament ? tournament.name : 'Tournament'}</small>
            <h1>School teams</h1>
          </div>
          <b>{total.toLocaleString()}</b>
        </header>
        {/*Place for render modal windows*/}
        {this.renderAddTeamModal()}
        {this.renderEditTeamModal()}
        {this.renderDeleteTeamErrorModal()}
        {this.renderDeleteTeamConfirmationModal()}
        <div className="row">
          <div className="col-md-12">{this.renderGrid()}</div>
        </div>
      </div>
    );
  }
}
